'use client';
import React from 'react';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';

interface Article {
  id: string;
  title: string;
  agency: string;
  category: string;
  updated: string;
  views: number;
  verified: boolean;
  tags: string[];
  status: string;
  summary: string;
}

const articleContent: Record<string, string[]> = {
  'kb-001': [
    'To update your mobile number in MySejahtera, the following steps must be completed:',
    '1. Open the MySejahtera application on your smartphone.',
    '2. Navigate to Profile Settings → Personal Information.',
    '3. Select "Update Mobile Number" and enter your new mobile number.',
    '4. A One-Time Password (OTP) will be sent to your existing registered number for verification.',
    '5. If you no longer have access to your existing number, proceed to the Account Recovery procedure.',
    '6. Complete the identity verification by providing your MyKad number and date of birth.',
    '7. Your new mobile number will be updated within 24 hours.',
    'Important: Citizens must visit the nearest Klinik Kesihatan or Hospital Kerajaan if they are unable to verify their identity digitally.',
  ],
  'kb-003': [
    'Malaysian passport renewal requires the following documents:',
    '1. Current passport (original)',
    '2. MyKad (original and photocopy)',
    '3. Completed application form (available at immigration counter or online)',
    '4. Payment of RM200 (5-year validity) or RM300 (10-year validity)',
    '5. 1 passport-size photograph (white background, 35mm × 50mm)',
    'Processing times:',
    '• Standard: 3–5 working days',
    '• Express (same-day): Available at selected counters with additional fee',
    'Appointments can be booked online through the MyImigresen portal or via the official mobile application.',
    'Note: Citizens aged 60 and above or those with disabilities may use the priority lane at all immigration offices.',
  ],
};

const defaultContent = [
  'This knowledge article provides official guidance for Malaysian Government services.',
  'Please ensure you have the required documents before proceeding.',
  'For further assistance, citizens may contact MyGCC at 1-800-88-MYGCC or visit the nearest service centre.',
  'This information is verified and maintained by the responsible Government agency.',
];

interface Props {
  article: Article;
  onClose: () => void;
}

export default function KnowledgeArticleDetail({ article, onClose }: Props) {
  const content = articleContent[article.id] || defaultContent;

  return (
    <div className="p-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[11px] bg-secondary text-primary px-2 py-0.5 rounded font-500">{article.category}</span>
            {article.verified && (
              <span className="text-[10px] bg-green-50 text-green-600 border border-green-200 px-1.5 py-0.5 rounded font-600 flex items-center gap-0.5">
                <Icon name="CheckBadgeIcon" size={9} />
                Verified Government Knowledge
              </span>
            )}
          </div>
          <h2 className="text-[16px] font-700 text-foreground leading-snug mb-1">{article.title}</h2>
          <div className="text-[12px] text-muted-foreground">{article.agency}</div>
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-muted rounded transition-colors flex-shrink-0">
          <Icon name="XMarkIcon" size={15} className="text-muted-foreground" />
        </button>
      </div>

      {/* Meta */}
      <div className="flex items-center gap-4 text-[11px] text-muted-foreground mb-4 pb-4 border-b border-border">
        <span className="flex items-center gap-1"><Icon name="CalendarIcon" size={11} />Updated: {article.updated}</span>
        <span className="flex items-center gap-1"><Icon name="EyeIcon" size={11} />{article.views.toLocaleString()} views</span>
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-600 ${article.status === 'Published' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}>{article.status}</span>
      </div>

      {/* Summary */}
      <div className="bg-secondary/50 border border-border rounded p-3 mb-4">
        <div className="text-[11px] font-700 uppercase tracking-wide text-muted-foreground mb-1">Summary</div>
        <p className="text-[13px] text-foreground leading-relaxed">{article.summary}</p>
      </div>

      {/* Content */}
      <div className="space-y-2 mb-4">
        <div className="text-[12px] font-700 uppercase tracking-wide text-muted-foreground">Detailed Guidance</div>
        {content.map((para, i) => (
          <p key={`para-${i}`} className="text-[13px] text-foreground leading-relaxed">{para}</p>
        ))}
      </div>

      {/* Tags */}
      <div className="mb-4">
        <div className="text-[11px] font-700 uppercase tracking-wide text-muted-foreground mb-2">Tags</div>
        <div className="flex flex-wrap gap-1.5">
          {article.tags.map((t) => (
            <span key={`artag-${article.id}-${t}`} className="text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded">{t}</span>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-3 border-t border-border">
        <button
          onClick={() => toast.success('Article shared with citizen context')}
          className="btn-primary px-3 py-1.5 text-[12px] flex items-center gap-1.5"
        >
          <Icon name="ShareIcon" size={13} />
          Use in Response
        </button>
        <button
          onClick={() => toast.info('Article copied to clipboard')}
          className="btn-secondary px-3 py-1.5 text-[12px] flex items-center gap-1.5"
        >
          <Icon name="ClipboardDocumentIcon" size={13} />
          Copy Link
        </button>
        <button
          onClick={() => toast.info('Feedback submitted — thank you')}
          className="btn-ghost px-3 py-1.5 text-[12px] border border-border flex items-center gap-1.5 ml-auto"
        >
          <Icon name="FlagIcon" size={13} />
          Report Issue
        </button>
      </div>
    </div>
  );
}